import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { ErrorBoundary } from 'bernie-view';
import { RouteStore } from 'bernie-core';

import { AlternateDatesModel, HotelViewModel } from 'models';

import { UitkCard, UitkCardContent } from 'uitk-react-cards';

import { AdditionalInfo } from './additional-info';
import { AlternateDates } from './alternate-dates';
import { BreakfastAndWifiDisclaimer } from './breakfast-wifi-disclaimer';
import { CardLink } from './card-link';
import { HighightedPropertyDetails } from './highlighted-property-details';
import { ListingCommonSection } from './listing-common-section';
import { ListingDescription } from './listing-description';
import { ListingErrorBoundary } from './listing-error-boundary';
import { ListingFooter } from './footer';
import { ListingName } from './listing-name';
import { LoyaltyMessage } from './loyalty-message';
import { Messaging } from 'components/common/messaging';
import { Neighborhood } from './neighborhood';
import { PlaceholderCard } from './placeholder-card';
import { PriceAndSupportingMessagesContainer } from './price-and-supporting-messages-container';
import { SearchResultsReviews } from './search-results-reviews';
import { Stars } from './star-rating';
import { VipAndPropertyTypeBadge } from './vip-and-property-badge';

interface DefaultListingProps {
  className: string;
  index?: number;
  loading?: boolean;
  model: HotelViewModel;
  routeStore?: RouteStore;
  isDatelessPdp?: boolean;
  defaultListingTopMarginClass?: string;
  lowerRight?: string;
  stars?: number;
  alternateDatesProps?: {
    handleClick: () => void;
    model: AlternateDatesModel[];
  };
  getTitleComponent: (imageAspectRatio?: string, extraProps?: any) => any;
  alternateDatesInfo: {
    collapsedLinkName: string;
    collapsedMsgKey: string;
    collapsedRfrr: string;
    expandedLinkName: string;
    expandedMsgKey: string;
    expandedRfrr: string;
    headerTextKey: string;
  };
  dataStid?: string;
  cardLinkProps?: any;
  routeStoreGoBack?: () => void;
  listingClickHandler?: () => void;
  setItemRef?: () => void;
}

export const DefaultListing: React.FunctionComponent<DefaultListingProps> = ({
  alternateDatesProps,
  index,
  loading,
  model,
  routeStore,
  isDatelessPdp,
  className,
  defaultListingTopMarginClass,
  lowerRight,
  stars,
  getTitleComponent,
  alternateDatesInfo,
  dataStid,
  cardLinkProps,
  routeStoreGoBack,
  listingClickHandler,
  setItemRef
}) => {
  const {
    alternateDates,
    disclaimerDialog,
    hotelDescription,
    listingFooter,
    loyaltyEarnAwards,
    name,
    neighborhood,
    price,
    primaryCardMessages,
    primaryCardMessagingModel,
    propertySummary,
    reviews,
    secondaryCardMessage,
    starRatingIcon,
    supportingMessages,
    thumbnail,
    vipMessage,
    propertyTypeBadge, // Experiment: 32599 Property_Type_Badging
    recentlyViewed
  } = model;

  const listingWrapperProps = {
    className,
    'data-stid': dataStid || '',
    ref: setItemRef,
    tabIndex: -1
  };
  const { loyaltyBaseEarnMessage, loyaltyBaseEarnAccessibilityMessage, loyaltyEarnIcon } = { ...loyaltyEarnAwards };

  const vipAndPropertyTypeBadgeProps = {
    vipMessage,
    propertyTypeBadge,
    recentlyViewed,
    isDatelessPdp
  };

  const listingNameProps = {
    name,
    ariaHidden: true,
    recentlyViewed
  };

  const highlightedPropertyDetailsProps = {
    propertyDetails: propertySummary
  };

  const priceAndSupportingMessagesContainerProps = {
    ...{ secondaryCardMessage, supportingMessages, price },
    wrapPrice: true,
    viewSmall: true,
    priceFontWeight: '600',
    hotelId: model.hotelId,
    recentlyViewed,
    isDatelessPdp
  };

  if (loading) {
    return (
      <li {...listingWrapperProps}>
        <PlaceholderCard index={index} />
      </li>
    );
  }

  const renderListingData = (lineCount?: number) => (
    <>
      <VipAndPropertyTypeBadge {...vipAndPropertyTypeBadgeProps} />
      <div className="uitk-grid">
        <div className="uitk-cell all-cell-fill uitk-type-300">
          <ListingName {...listingNameProps} lineCount={lineCount} />
        </div>
      </div>
      <div className="uitk-grid">
        <div className="uitk-cell all-cell-1-2 uitk-type-300">
          <HighightedPropertyDetails {...highlightedPropertyDetailsProps} />
          <Neighborhood neighborhood={neighborhood} />
        </div>
      </div>
      <div className="uitk-grid">
        <div className="uitk-cell all-cell-fill all-cell-align-bottom uitk-type-300">
          <Stars stars={stars} starRatingIcon={starRatingIcon} />
          <ListingCommonSection model={model} routeStore={routeStore} routeStoreGoBack={routeStoreGoBack} />
        </div>
        <div className="uitk-cell all-cell-fill uitk-type-right all-cell-align-bottom">
          <PriceAndSupportingMessagesContainer {...priceAndSupportingMessagesContainerProps} />
          {lowerRight && !isDatelessPdp && <div className="uitk-cell all-cell-shrink all-cell-align-bottom">{lowerRight}</div>}
        </div>
      </div>
    </>
  );

  return (
    <li {...listingWrapperProps}>
      <UitkCard className="has-action listing__card">
        <ListingName name={name} visuallyHidden={true} />
        <div className="link-container">
          <ListingErrorBoundary thumbnail={thumbnail} name={name}>
            {getTitleComponent()}
          </ListingErrorBoundary>
          <UitkCardContent className={`all-y-padding-three all-x-padding-three listing-content ${defaultListingTopMarginClass}`}>
            <AbacusExperiment name="SRP_propertyName_SingleCol">
              <AbacusControl>
                <>
                  <VipAndPropertyTypeBadge {...vipAndPropertyTypeBadgeProps} />
                  <div className="uitk-grid">
                    <div className="uitk-cell all-cell-fill uitk-type-300">
                      <ListingName {...listingNameProps} />
                      <HighightedPropertyDetails {...highlightedPropertyDetailsProps} />
                      <Neighborhood neighborhood={neighborhood} />
                    </div>
                    <div className="uitk-cell all-cell-fill uitk-type-right">
                      <PriceAndSupportingMessagesContainer {...priceAndSupportingMessagesContainerProps} />
                    </div>
                  </div>
                  <div className="uitk-grid">
                    <div className="uitk-cell all-cell-fill uitk-type-300">
                      <Stars stars={stars} starRatingIcon={starRatingIcon} />
                      <ErrorBoundary label="hotel.search.listing.messaging">
                        <Messaging
                          className="listing__messaging uitk-grid uitk-type-300"
                          primaryMessaging={primaryCardMessagingModel}
                          primaryMessagingList={primaryCardMessages}
                        />
                      </ErrorBoundary>
                      <LoyaltyMessage
                        loyaltyEarnIcon={loyaltyEarnIcon}
                        loyaltyBaseEarnAccessibilityMessage={loyaltyBaseEarnAccessibilityMessage}
                        loyaltyBaseEarnMessage={loyaltyBaseEarnMessage}
                      />
                    </div>
                    {lowerRight && !isDatelessPdp && <div className="uitk-cell all-cell-shrink all-cell-align-bottom">{lowerRight}</div>}
                  </div>
                  <BreakfastAndWifiDisclaimer
                    disclaimerDialog={disclaimerDialog}
                    handleBack={routeStoreGoBack}
                    hotelId={model.hotelId}
                    routeStore={routeStore}
                  />
                  <ListingDescription description={hotelDescription} />
                  <ErrorBoundary label="hotel.search.listing.reviews">
                    <SearchResultsReviews className="listing__reviews all-t-margin-two" reviews={reviews} />
                  </ErrorBoundary>
                </>
              </AbacusControl>
              <AbacusVariant bucket={1}>{renderListingData()}</AbacusVariant>
              <AbacusVariant bucket={2}>{renderListingData(2)}</AbacusVariant>
            </AbacusExperiment>
          </UitkCardContent>
          <CardLink {...cardLinkProps} index={index} listingClickHandler={listingClickHandler} />
        </div>
        <ListingFooter {...cardLinkProps} listingFooter={listingFooter} listingClickHandler={listingClickHandler} />
        {alternateDates && (
          <AdditionalInfo {...alternateDatesInfo} displayHeaderText={false} keepOpen={false}>
            <AlternateDates {...alternateDatesProps} />
          </AdditionalInfo>
        )}
      </UitkCard>
    </li>
  );
};
